/**
 * Ride replay: how long a finished order's journey is kept, and what the Journeys report
 * calls a problem.
 *
 * Two kinds of setting on one page, because they are read together. The retention is a
 * promise — a journey is a record of where a driver was, minute by minute — and a new value
 * applies to journeys written from now on, never to ones already kept, as with recordings.
 * The thresholds are a judgement: what counts as late, as the long way round, as a long wait,
 * here, for this firm. They change what the report flags, not what was recorded, so moving one
 * re-reads every ride in the period by the new rule the next time the report is opened.
 *
 * One request saves the lot, under the `SaveBar`, like the other settings pages.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { journeyLimits, settingsApi, type JourneySettings } from '../api';
import { checkForm, named } from '../check';
import { refusal } from '../errors';
import {
  Banner,
  EmptyState,
  FieldErrors,
  Measured,
  PageHeader,
  SaveBar,
  Section,
  SettingRow,
  type FieldProblem,
} from '../../ui';

export function ReplayPage() {
  const { t } = useTranslation();
  const page = useRef<HTMLDivElement>(null);

  const [loaded, setLoaded] = useState<JourneySettings | null>(null);
  const [draft, setDraft] = useState<JourneySettings | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<FieldProblem | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const settings = await settingsApi.journeys();

        setLoaded(settings);
        setDraft(settings);
      } catch (failure) {
        setError(refusal(failure, t, 'settings.couldNotLoad'));
      }
    })();
  }, [t]);

  useEffect(() => {
    if (!saved) return;

    const timer = globalThis.setTimeout(() => setSaved(false), 4000);

    return () => globalThis.clearTimeout(timer);
  }, [saved]);

  const change = useCallback(
    <K extends keyof JourneySettings>(key: K, value: JourneySettings[K]) => {
      setSaved(false);
      setDraft((current) => (current === null ? current : { ...current, [key]: value }));
    },
    [],
  );

  const changes = useMemo(() => {
    if (loaded === null || draft === null) return 0;

    return (Object.keys(loaded) as (keyof JourneySettings)[]).filter(
      (key) => loaded[key] !== draft[key],
    ).length;
  }, [loaded, draft]);

  async function save() {
    if (draft === null) return;

    const invalid = checkForm(page.current, t);

    if (invalid !== null) {
      setError(invalid);
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const stored = await settingsApi.saveJourneys(draft);

      setLoaded(stored);
      setDraft(stored);
      setSaved(true);
    } catch (failure) {
      setError(named(refusal(failure, t, 'settings.couldNotSave'), page.current, t));
    } finally {
      setSaving(false);
    }
  }

  return (
    <FieldErrors root={page} problem={error} onEdit={() => setError(null)}>
      <PageHeader title={t('nav.settings.replay')} description={t('nav.settingsAbout.replay')} />

      {error !== null && <Banner tone="danger">{error.message}</Banner>}
      {saved && <Banner tone="ok">{t('settings.saved')}</Banner>}

      {draft === null ? (
        error === null && <EmptyState>{t('app.loading')}</EmptyState>
      ) : (
        <form
          noValidate
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            void save();
          }}
        >
          <Section title={t('replay.keepTitle')} description={t('replay.keepHelp')}>
            <NumberSetting
              id="retentionDays"
              value={draft.retentionDays}
              unit="settings.unitDays"
              last
              onChange={(v) => change('retentionDays', v)}
            />
          </Section>

          <Section title={t('replay.problemsTitle')} description={t('replay.problemsHelp')}>
            <NumberSetting
              id="lateMinutes"
              value={draft.lateMinutes}
              unit="settings.unitMinutes"
              onChange={(v) => change('lateMinutes', v)}
            />
            <NumberSetting
              id="detourPercent"
              value={draft.detourPercent}
              unit="replay.unitPercent"
              onChange={(v) => change('detourPercent', v)}
            />
            <NumberSetting
              id="detourMinimumMetres"
              value={draft.detourMinimumMetres}
              unit="settings.unitMetres"
              onChange={(v) => change('detourMinimumMetres', v)}
            />
            <NumberSetting
              id="longWaitMinutes"
              value={draft.longWaitMinutes}
              unit="settings.unitMinutes"
              onChange={(v) => change('longWaitMinutes', v)}
            />
            <NumberSetting
              id="longStopMinutes"
              value={draft.longStopMinutes}
              unit="settings.unitMinutes"
              onChange={(v) => change('longStopMinutes', v)}
            />
            <NumberSetting
              id="inTownSpeedKmh"
              value={draft.inTownSpeedKmh}
              unit="replay.unitKmh"
              onChange={(v) => change('inTownSpeedKmh', v)}
            />
            <NumberSetting
              id="outOfTownSpeedKmh"
              value={draft.outOfTownSpeedKmh}
              unit="replay.unitKmh"
              last
              onChange={(v) => change('outOfTownSpeedKmh', v)}
            />
          </Section>

          <SaveBar
            changes={changes}
            problem={error?.message}
            busy={saving}
            discardLabel={t('settings.discard')}
            saveLabel={saving ? t('settings.saving') : t('settings.save')}
            onDiscard={() => {
              setSaved(false);
              setError(null);
              setDraft(loaded);
            }}
            onSave={() => void save()}
          >
            {t('settings.unsaved', { count: changes })}
          </SaveBar>
        </form>
      )}
    </FieldErrors>
  );
}

function NumberSetting({
  id,
  value,
  unit,
  last,
  onChange,
}: {
  id: keyof typeof journeyLimits;
  value: number;
  unit: string;
  last?: boolean;
  onChange: (value: number) => void;
}) {
  const { t } = useTranslation();
  const limits = journeyLimits[id];

  return (
    <SettingRow label={t(`replay.${id}`)} help={t(`replay.${id}Help`)} last={last}>
      <Measured
        id={id}
        unit={t(unit)}
        type="number"
        inputMode="numeric"
        aria-label={t(`replay.${id}`)}
        required
        value={Number.isNaN(value) ? '' : value}
        min={limits.min}
        max={limits.max}
        onChange={(event) => onChange(event.target.valueAsNumber)}
      />
    </SettingRow>
  );
}
